// ABOUTME: Mobile hamburger menu overlay
// ABOUTME: Full navigation menu that slides in from right on mobile

"use client"

import { useEffect, useMemo, useRef } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { SignOutButton, UserButton } from "@clerk/nextjs"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faHouse,
  faCompass,
  faBookmark,
  faUser,
  faGear,
  faRightFromBracket,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"

interface MobileMenuProps {
  isOpen: boolean
  onClose: () => void
  username?: string
  unreadNotificationsCount?: number
  showOnDesktop?: boolean
}

// Memoize UserButton appearance outside component to prevent recreation
const userButtonAppearance = {
  elements: {
    avatarBox: "w-8 h-8",
  },
}

export function MobileMenu({ isOpen, onClose, username, showOnDesktop = false }: MobileMenuProps) {
  const pathname = usePathname()
  const prevPathnameRef = useRef(pathname)

  // Close on pathname change (only when pathname actually changes, not on every render)
  useEffect(() => {
    if (isOpen && pathname !== prevPathnameRef.current) {
      onClose()
    }
    prevPathnameRef.current = pathname
  }, [pathname, isOpen, onClose])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
      return () => {
        document.body.style.overflow = ""
      }
    }
  }, [isOpen])

  // Close on escape key
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleEscape)
    return () => window.removeEventListener("keydown", handleEscape)
  }, [isOpen, onClose])

  // Memoize navItems
  const navItems = useMemo(() => [
    { href: "/home", icon: faHouse, label: "Home" },
    { href: "/discover", icon: faCompass, label: "Discover" },
  ], [])

  if (!isOpen) return null

  const hideClass = showOnDesktop ? "" : "md:hidden"

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${hideClass} fixed inset-0 bg-black/50 z-50`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Menu Panel */}
      <div className={`${hideClass} fixed top-0 right-0 bottom-0 w-72 bg-white dark:bg-neutral-900 z-50 shadow-xl animate-slide-in-right`}>
        {/* Header */}
        <div className="h-14 px-4 flex items-center justify-between border-b border-gray-200 dark:border-neutral-800">
          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Menu</span>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                ${pathname === item.href
                  ? "bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100"
                }
              `}
            >
              <FontAwesomeIcon icon={item.icon} className="w-5 h-5" />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}
        </nav>

        {/* Divider */}
        <div className="mx-4 h-px bg-gray-200 dark:bg-neutral-800" />

        {/* User Section */}
        <div className="p-4 space-y-1">
          {username && (
            <Link
              href={`/@${username}`}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                ${pathname === `/@${username}`
                  ? "bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100"
                }
              `}
            >
              <FontAwesomeIcon icon={faUser} className="w-5 h-5" />
              <span className="text-sm font-medium">My Blog</span>
            </Link>
          )}
          <Link
            href="/bookmarks"
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
              ${pathname === "/bookmarks"
                ? "bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100"
              }
            `}
          >
            <FontAwesomeIcon icon={faBookmark} className="w-5 h-5" />
            <span className="text-sm font-medium">Bookmarks</span>
          </Link>
          <Link
            href="/settings"
            className={`
              flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
              ${pathname === "/settings"
                ? "bg-gray-100 dark:bg-neutral-800 text-gray-900 dark:text-gray-100"
                : "text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100"
              }
            `}
          >
            <FontAwesomeIcon icon={faGear} className="w-5 h-5" />
            <span className="text-sm font-medium">Settings</span>
          </Link>
        </div>

        {/* Divider */}
        <div className="mx-4 h-px bg-gray-200 dark:bg-neutral-800" />

        {/* Account Section */}
        <div className="p-4 space-y-3">
          {/* Clerk UserButton for account management */}
          <div className="flex items-center gap-3 px-4 py-2">
            <UserButton
              afterSignOutUrl="/"
              appearance={userButtonAppearance}
            />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Account</span>
          </div>

          {/* Sign Out as fallback */}
          <SignOutButton>
            <button className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-neutral-800 hover:text-gray-900 dark:hover:text-gray-100 transition-colors">
              <FontAwesomeIcon icon={faRightFromBracket} className="w-5 h-5" />
              <span className="text-sm font-medium">Sign Out</span>
            </button>
          </SignOutButton>
        </div>
      </div>
    </>
  )
}
